import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export const FW_META = {
  iso27001: { short: "ISO 27001", cls: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30" },
  soc2: { short: "SOC 2", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  iso9001: { short: "ISO 9001", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30" },
  dpdp: { short: "DPDP/GDPR", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  iso14001: { short: "ISO 14001", cls: "bg-lime-500/15 text-lime-700 dark:text-lime-300 border-lime-500/30" },
  iso45001: { short: "ISO 45001", cls: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30" },
  naac: { short: "NAAC", cls: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/30" },
  hipaa: { short: "HIPAA", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30" },
  pcidss: { short: "PCI-DSS", cls: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30" },
  nistcsf: { short: "NIST CSF", cls: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30" },
  nba: { short: "NBA", cls: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/30" },
  iso21001: { short: "ISO 21001", cls: "bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/30" },
  cmmi: { short: "CMMI", cls: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30" },
  iatf16949: { short: "IATF 16949", cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30" },
  as9100: { short: "AS9100", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" },
};

const STATUS_CLS = {
  ok: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  bad: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
  info: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
  na: "bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500/30",
};
const STATUS_TONE = {
  implemented: "ok", approved: "ok", closed: "ok", pass: "ok", done: "ok", fulfilled: "ok", accepted: "ok", resolved: "ok", low: "ok", completed: "ok",
  partial: "warn", in_progress: "warn", in_review: "warn", contained: "warn", medium: "warn", minor: "warn", due_soon: "warn",
  not_started: "bad", open: "bad", fail: "bad", major: "bad", high: "bad", critical: "bad", overdue: "bad",
  verification: "info", planned: "info", pending: "info", draft: "info", observation: "info",
  na: "na", not_applicable: "na", none: "na",
};

export function StatusBadge({ status, className, children, ...rest }) {
  const tone = STATUS_TONE[status] || "na";
  return (
    <Badge variant="outline" className={cn("font-mono text-[10px] uppercase tracking-wider rounded-md", STATUS_CLS[tone], className)} {...rest}>
      {children ?? String(status || "—").replace(/_/g, " ")}
    </Badge>
  );
}

export function FrameworkBadge({ code, className }) {
  const m = FW_META[code] || { short: code, cls: STATUS_CLS.na };
  return <Badge variant="outline" className={cn("rounded-md font-medium text-[11px]", m.cls, className)} data-testid={`fw-badge-${code}`}>{m.short}</Badge>;
}

export function PageHeader({ eyebrow, title, subtitle, actions, testId }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 fade-up" data-testid={testId}>
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = "default", testId }) {
  const tones = { default: "text-foreground", ok: "text-emerald-600 dark:text-emerald-400", warn: "text-amber-600 dark:text-amber-400", bad: "text-rose-600 dark:text-rose-400" };
  return (
    <div className="surface p-5 flex items-start justify-between gap-3 fade-up" data-testid={testId}>
      <div>
        <div className="eyebrow">{label}</div>
        <div className={cn("text-3xl font-display font-bold mt-2 tabular-nums", tones[tone])}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      {Icon && <div className="p-2 rounded-md bg-muted text-muted-foreground"><Icon size={18} /></div>}
    </div>
  );
}

export function EmptyState({ title, hint, action, testId }) {
  return (
    <div className="surface border-dashed p-10 text-center" data-testid={testId}>
      <div className="font-display font-semibold">{title}</div>
      {hint && <p className="text-sm text-muted-foreground mt-1">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function ControlMultiSelect({ controls, value = [], onChange, testId = "control-multiselect" }) {
  const toggle = (code) => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  return (
    <div className="border rounded-md max-h-44 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-1 bg-background" data-testid={testId}>
      {controls.map((c) => (
        <label key={c.control_code || c.code} className="flex items-center gap-2 text-xs cursor-pointer rounded px-1.5 py-1 row-hover">
          <input type="checkbox" className="accent-[hsl(var(--primary))]" checked={value.includes(c.control_code || c.code)} onChange={() => toggle(c.control_code || c.code)} data-testid={`${testId}-${c.control_code || c.code}`} />
          <span className="font-mono text-[10px] text-muted-foreground">{c.control_code || c.code}</span>
          <span className="truncate">{c.title}</span>
        </label>
      ))}
    </div>
  );
}

export function ReadinessBar({ percent, color = "indigo" }) {
  const colors = { indigo: "bg-indigo-500", emerald: "bg-emerald-500", violet: "bg-violet-500", amber: "bg-amber-500", lime: "bg-lime-500", orange: "bg-orange-500", rose: "bg-rose-500", cyan: "bg-cyan-500", slate: "bg-slate-500", fuchsia: "bg-fuchsia-500", pink: "bg-pink-500", teal: "bg-teal-500", red: "bg-red-500", sky: "bg-sky-500" };
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div className={cn("h-full rounded-full", colors[color] || "bg-primary")} style={{ width: `${percent}%`, transition: "width 600ms ease-out" }} />
    </div>
  );
}
